import React, { useEffect, useState } from "react";
import { useApi } from "../../context/ApiContext";
import { useNavigate, useParams } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";

export default function PropertyDetailAdmin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const {
    adminGetProperty: fetchProperty,
    adminListUnits: fetchUnits,
    adminCreateUnit,
  } = useApi();

  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [importingNow, setImportingNow] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0, errors: [] });
  const [roomTypes, setRoomTypes] = useState(() => {
    try {
      const raw = localStorage.getItem("roomTypes");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [bulkCount, setBulkCount] = useState(1);
  const [bulkPrefix, setBulkPrefix] = useState("");
  const [bulkStart, setBulkStart] = useState(1);
  const [newUnit, setNewUnit] = useState({
    name: "",
    beds: 0,
    baths: 0,
    sqft: 0,
    floor: 0,
    price: 0,
    available: true,
  });

  async function load() {
    try {
      setLoading(true);
      setErr("");
      const [p, u] = await Promise.all([fetchProperty(slug), fetchUnits(slug)]);
      setProperty(p || null);
      setUnits(Array.isArray(u) ? u : []);
    } catch (e) {
      setErr(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      await load();
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function onAddUnit(e) {
    e.preventDefault();
    try {
      const payload = {
        ...newUnit,
        beds: Number(newUnit.beds || 0),
        baths: Number(newUnit.baths || 0),
        sqft: Number(newUnit.sqft || 0),
        floor: Number(newUnit.floor || 0),
        price: Number(newUnit.price || 0),
      };
      const rt = roomTypes.find((t) => t.id === selectedTypeId);
      if (rt) {
        payload.roomTypeId = rt.id;
        payload.roomTypeName = rt.name;
        if (!payload.name) payload.name = rt.name;
        if (!payload.beds) payload.beds = Number(rt.beds || 0);
        if (!payload.baths) payload.baths = Number(rt.baths || 0);
        if (!payload.sqft) payload.sqft = Number(rt.sqft || 0);
        if (!payload.price) payload.price = Number(rt.basePrice || 0);
      }

      const qty = Math.max(1, Number(bulkCount || 1));
      const start = Math.max(1, Number(bulkStart || 1));
      const createdUnits = [];
      for (let i = 0; i < qty; i++) {
        const suffix = qty > 1 ? String(start + i) : "";
        const named = {
          ...payload,
          name: payload.name || (bulkPrefix ? `${bulkPrefix} ${suffix}` : `Unit ${suffix}`),
        };
        const created = await adminCreateUnit(slug, named);
        createdUnits.push(created || { id: `temp-${Date.now()}-${i}`, ...named });
      }
      setUnits((prev) => [...prev, ...createdUnits]);
      setAdding(false);
      setNewUnit({ name: "", beds: 0, baths: 0, sqft: 0, floor: 0, price: 0, available: true });
      setSelectedTypeId("");
      setBulkCount(1);
      setBulkPrefix("");
      setBulkStart(1);
    } catch (e) {
      alert(e?.message || "Failed to create unit");
    }
  }

  // --- CSV helpers inside component scope ---
  function parseCsv(text) {
    if (!text) return [];
    const rows = [];
    let cur = [];
    let val = "";
    let inQuotes = false;
    const pushVal = () => { cur.push(val); val = ""; };
    const pushRow = () => { rows.push(cur); cur = []; };
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { val += '"'; i++; }
          else { inQuotes = false; }
        } else { val += c; }
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ',') { pushVal(); }
        else if (c === '\n' || c === '\r') {
          if (val !== "" || cur.length) pushVal();
          if (cur.length) pushRow();
          while (text[i + 1] === '\n' || text[i + 1] === '\r') i++;
        } else { val += c; }
      }
    }
    if (val !== "" || cur.length) { pushVal(); pushRow(); }
    if (rows.length === 0) return [];
    const header = rows[0].map((h) => h.trim());
    return rows.slice(1).filter(r => r.length && r.some(x => String(x).trim() !== "")).map((r) => {
      const obj = {};
      for (let i = 0; i < header.length; i++) obj[header[i]] = (r[i] ?? "").toString().trim();
      return obj;
    });
  }

  async function startImport(e) {
    if (e) e.preventDefault();
    try {
      setImportingNow(true);
      const rows = parsedRows || [];
      setImportProgress({ done: 0, total: rows.length, errors: [] });

      const created = [];
      let done = 0;
      const errs = [];

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i] || {};
        try {
          const payload = {
            name: (r.name || "").trim(),
            beds: Number(r.beds || 0),
            baths: Number(r.baths || 0),
            sqft: Number(r.sqft || 0),
            floor: Number(r.floor || 0),
            price: Number(r.price || 0),
            available: (() => { const v = String(r.available || "").toLowerCase().trim(); return v === 'true' || v === '1' || v === 'yes' || v === 'y'; })(),
          };
          // match room type by name or id
          const nameKey = (r.roomTypeName || r.roomType || "").toString().toLowerCase().trim();
          const idKey = (r.roomTypeId || "").toString().trim();
          let rt = null;
          if (nameKey) rt = roomTypes.find(t => (t.name || "").toLowerCase().trim() === nameKey) || null;
          if (!rt && idKey) rt = roomTypes.find(t => String(t.id) === idKey) || null;
          if (rt) {
            payload.roomTypeId = rt.id;
            payload.roomTypeName = rt.name;
            if (!payload.name) payload.name = rt.name;
            if (!payload.beds) payload.beds = Number(rt.beds || 0);
            if (!payload.baths) payload.baths = Number(rt.baths || 0);
            if (!payload.sqft) payload.sqft = Number(rt.sqft || 0);
            if (!payload.price) payload.price = Number(rt.basePrice || 0);
          }

          const createdUnit = await adminCreateUnit(slug, payload);
          created.push(createdUnit || payload);
        } catch (err) {
          errs.push({ index: i, message: err?.message || String(err) });
        } finally {
          done++;
          setImportProgress({ done, total: rows.length, errors: errs });
        }
      }

      if (created.length) setUnits(prev => [...prev, ...created]);
      setImporting(false);
      setParsedRows([]);
      setImportText("");
    } finally {
      setImportingNow(false);
    }
  }

  if (loading) return <div>Loadingâ€¦</div>;
  if (err) return <div className="text-red-600">{err}</div>;
  if (!property) return <div>Not found.</div>;

  const unitsAvailable = units.filter(u => u.available).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{property.name}</h1>
          <p className="text-gray-600">
            {property.slug} â€¢ {property.category} â€¢ {property.status} â€¢ {unitsAvailable} unit(s) available
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/admin/properties/${property.slug}/edit`)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border"
          >
            <Pencil className="w-4 h-4" />
            Edit Property
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Info label="Location" value={property.location || "-"} />
        <Info label="Address" value={property.address || "-"} />
        <Info label="Agent" value={property.agentName || "-"} />
        <Info label="Email" value={property.agentEmail || "-"} />
        <Info label="Phone" value={property.agentPhone || "-"} />
        <Info label="Whatsapp" value={property.agentWhatsapp || "-"} />
      </div>

      {property.description && (
        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold mb-2">Description</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{property.description}</p>
        </div>
      )}

      <section className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Units</h2>
          <button
            onClick={() => setAdding((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-black text-white"
          >
            <Plus className="w-4 h-4" />
            Add Unit
          </button>
          <button
            onClick={() => setImporting((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border ml-2"
          >
            Import CSV
          </button>
        </div>

        {importing && (
          <div className="border rounded-md p-3 mb-4 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">Bulk Import from CSV</div>
              <button onClick={() => { setImporting(false); setParsedRows([]); setImportText(""); }} className="text-sm text-gray-600">Close</button>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              Paste CSV with headers: name,beds,baths,sqft,floor,price,available,roomTypeName
            </p>
            <div className="grid md:grid-cols-3 gap-3 mb-2">
              <div className="md:col-span-2">
                <textarea
                  rows={6}
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="name,beds,baths,sqft,floor,price,available,roomTypeName\nUnit 101,1,1,35,1,7500,true,1 Bedroom"
                  className="w-full border rounded-md px-3 py-2 font-mono text-xs"
                />
              </div>
              <div>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const text = await f.text();
                    setImportText(text);
                  }}
                  className="w-full border rounded-md px-3 py-2"
                />
                <button
                  type="button"
                  onClick={() => {
                    const rows = parseCsv(importText);
                    setParsedRows(rows);
                  }}
                  className="mt-2 w-full px-3 py-2 rounded-md bg-black text-white"
                >
                  Parse CSV
                </button>
              </div>
            </div>
            {parsedRows.length > 0 && (
              <div className="text-sm">
                <div className="mb-2">Parsed {parsedRows.length} rows. Preview first 5:</div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs bg-white border rounded-md">
                    <thead className="bg-gray-100">
                      <tr>
                        {Object.keys(parsedRows[0] || {}).map((k) => (
                          <th key={k} className="text-left px-2 py-1 capitalize">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0,5).map((r, i) => (
                        <tr key={i} className="border-t">
                          {Object.keys(parsedRows[0]).map((k) => (
                            <td key={k} className="px-2 py-1">{String(r[k] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={importingNow}
                    onClick={startImport}
                    className="px-3 py-2 rounded-md bg-green-600 text-white disabled:opacity-60"
                  >
                    {importingNow ? `Importing ${importProgress.done}/${importProgress.total}...` : "Start Import"}
                  </button>
                  {importProgress.errors.length > 0 && (
                    <span className="text-red-600">{importProgress.errors.length} error(s)</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {adding && (
          <form onSubmit={onAddUnit} className="grid md:grid-cols-6 gap-3 border rounded-md p-3 mb-4">
            {roomTypes.length > 0 && (
              <div className="col-span-full grid md:grid-cols-6 gap-3">
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Type (optional)</label>
                  <select
                    value={selectedTypeId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedTypeId(id);
                      const rt = roomTypes.find((t) => t.id === id);
                      if (rt) {
                        setNewUnit((u) => ({
                          ...u,
                          name: u.name || rt.name,
                          beds: Number(rt.beds || 0),
                          baths: Number(rt.baths || 0),
                          sqft: Number(rt.sqft || 0),
                          price: Number(rt.basePrice || 0),
                        }));
                      }
                    }}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">â€” Select template â€”</option>
                    {roomTypes.map((rt) => (
                      <option key={rt.id} value={rt.id}>
                        {rt.name} â€¢ {rt.beds} bed â€¢ {rt.baths} bath â€¢ {rt.sqft} sqm
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qty</label>
                    <input type="number" min={1} value={bulkCount} onChange={(e) => setBulkCount(Number(e.target.value || 1))} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start #</label>
                    <input type="number" min={1} value={bulkStart} onChange={(e) => setBulkStart(Number(e.target.value || 1))} className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name Prefix</label>
                    <input value={bulkPrefix} onChange={(e) => setBulkPrefix(e.target.value)} className="w-full border rounded-md px-3 py-2" placeholder="e.g. Unit" />
                  </div>
                </div>
              </div>
            )}
            <Input label="Name" value={newUnit.name} onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })} />
            <Input type="number" label="Beds" value={newUnit.beds} onChange={(e) => setNewUnit({ ...newUnit, beds: e.target.value })} />
            <Input type="number" label="Baths" value={newUnit.baths} onChange={(e) => setNewUnit({ ...newUnit, baths: e.target.value })} />
            <Input type="number" label="Sqft" value={newUnit.sqft} onChange={(e) => setNewUnit({ ...newUnit, sqft: e.target.value })} />
            <Input type="number" label="Floor" value={newUnit.floor} onChange={(e) => setNewUnit({ ...newUnit, floor: e.target.value })} />
            <Input type="number" label="Price" value={newUnit.price} onChange={(e) => setNewUnit({ ...newUnit, price: e.target.value })} />
            <div className="col-span-full flex items-center gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newUnit.available}
                  onChange={(e) => setNewUnit({ ...newUnit, available: e.target.checked })}
                />
                Available
              </label>
              <div className="ml-auto flex gap-2">
                <button type="button" onClick={() => setAdding(false)} className="px-3 py-2 border rounded-md">
                  Cancel
                </button>
                <button type="submit" className="px-3 py-2 rounded-md bg-black text-white">
                  Save Unit
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Beds</th>
                <th className="text-left px-4 py-2">Baths</th>
                <th className="text-left px-4 py-2">Sqft</th>
                <th className="text-left px-4 py-2">Floor</th>
                <th className="text-left px-4 py-2">Price</th>
                <th className="text-left px-4 py-2">Available</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {units.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-gray-500" colSpan={8}>
                    No units yet.
                  </td>
                </tr>
              ) : (
                units.map((u) => {
                  const id = u.id || u.sk;
                  return (
                    <tr key={id} className="border-t">
                      <td className="px-4 py-2 font-medium">{u.name}</td>
                      <td className="px-4 py-2">{u.beds}</td>
                      <td className="px-4 py-2">{u.baths}</td>
                      <td className="px-4 py-2">{u.sqft}</td>
                      <td className="px-4 py-2">{u.floor}</td>
                      <td className="px-4 py-2">{u.price}</td>
                      <td className="px-4 py-2">{u.available ? "Yes" : "No"}</td>
                      <td className="px-4 py-2 text-right text-gray-400">â€”</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-xs uppercase text-gray-500">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
function Input({ label, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input {...rest} className="w-full border rounded-md px-3 py-2" />
    </div>
  );
}


